/**
 * Utility functions for generating request links.
 * Mirrors the functionality from the Angular requestItemCtrl.
 */

import { type ClientRequest, generalRequestType } from './ClientRequestService';
import { requestTypeLinkUrl, serviceBubbleTypes } from './const';
import env from 'bench-environment';

/**
 * Properties for a service bubble URL block.
 * This structure is used for generating rich links in the messaging system.
 */
interface ServiceBubbleProperties {
  requestId?: string;
  hasLink?: boolean;
}

/**
 * Represents a URL block with additional metadata for service bubbles.
 */
interface UrlBlock {
  url: string;
  title: string;
  serviceBubbleType: string;
  serviceBubbleProperties: ServiceBubbleProperties;
}

/**
 * Generates a URL block from a request object.
 * This mirrors the getUrlBlockFromRequest method from the Angular controller.
 *
 * @param baseUrl - The base URL for the application (e.g., CWA base URL)
 * @param request - The client request object
 * @returns An object containing the URL and metadata
 */
export function getUrlBlockFromRequest(baseUrl: string, request: Partial<ClientRequest>): UrlBlock {
  const requestType = request.requestType || 'GENERAL';
  const requestPath = requestTypeLinkUrl[requestType as keyof typeof requestTypeLinkUrl] || '';
  const clientId = request.clientId || '';
  const requestId = request.id || '';

  const isGeneralRequest = requestType === generalRequestType;
  const isGeneralRequestWithUrl =
    isGeneralRequest && request.metadata?.url && request.metadata.url.length > 0;

  // Use custom URL for general requests with a URL, otherwise construct from base
  const fallbackUrl = isGeneralRequestWithUrl
    ? request.metadata!.url
    : `${baseUrl}/${clientId}/${requestPath}`;

  const title = request.name || 'Request';

  const serviceBubbleProperties: ServiceBubbleProperties = {
    requestId,
  };

  if (isGeneralRequestWithUrl) {
    serviceBubbleProperties.hasLink = true;
  }

  return {
    url: fallbackUrl,
    title,
    serviceBubbleType:
      serviceBubbleTypes[requestType as keyof typeof serviceBubbleTypes] || 'GeneralRequest',
    serviceBubbleProperties,
  };
}

/**
 * Generates a request link string from a request object.
 * This is a simplified version that returns just the URL string.
 *
 * @param request - The client request object
 * @returns The generated URL string
 */
export function generateRequestLink(request: Partial<ClientRequest>): string {
  const baseUrl = env.location.cwa;
  return getUrlBlockFromRequest(baseUrl, request).url;
}
